import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Type definitions
interface PipelineStageError {
  message: string;
  code: string;
  retryable: boolean;
  timestamp: Date;
  details?: Record<string, any>;
}

interface PipelineSubStage {
  id: string;
  name: string;
  type: 'api_call' | 'filter' | 'calculation' | 'enrichment';
  provider?: 'alpaca' | 'alpha_vantage' | 'polygon' | 'internal';
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  inputCount?: number;
  outputCount?: number;
  duration?: number;
  startedAt?: Date;
  completedAt?: Date;
  error?: string;
  metadata?: Record<string, any>;
}

interface PipelineStage {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  inputCount: number;
  outputCount: number;
  conversionRate: number;
  duration: number;
  startedAt?: Date;
  completedAt?: Date;
  subStages?: PipelineSubStage[];
  filteredReasons?: string[];
  error?: PipelineStageError;
  metadata?: Record<string, any>;
}

/** Process step from API stage metadata (no hardcoded values in frontend). */
interface StageProcessStep {
  name: string;
  type: 'api_call' | 'filter' | 'calculation' | 'enrichment';
  provider?: 'alpaca' | 'alpha_vantage' | 'polygon' | 'internal';
  description: string;
}

/** Stage display metadata from pipeline-status API (single source of truth). */
interface StageMetadata {
  purpose: string;
  apiSummary: string;
  processSteps: StageProcessStep[];
  /** True when stage is market-level (no stock list); from backend trading universe / enhanced regime. */
  isMarketLevel?: boolean;
}

interface TradingUniversePipelineStatus {
  lastBuildTime: Date | null;
  nextBuildTime: Date | null;
  scheduledTimes: string[];
  stages: PipelineStage[];
  totalDuration: number;
  finalCount: number;
  status: 'idle' | 'running' | 'success' | 'failed';
  buildId?: string;
  regimeLabel?: string;
  seatAllocation?: {
    sticky: number;
    fresh: number;
    total: number;
  };
  /** Stage purpose, API summary, process steps keyed by stage id. From backend (TRADING_UNIVERSE_STAGE_METADATA). */
  stageMetadata?: Record<string, StageMetadata>;
  /** When Redis has no pipeline run, latest stored universe from DB. */
  lastStoredUniverse?: {
    createdAt: Date | string;
    totalStocks: number;
  };
}

interface UserScreeningPipelineStatus {
  lastRunTime: Date | null;
  userId: string;
  userEmail?: string;
  stages: PipelineStage[];
  totalDuration: number;
  status: 'idle' | 'running' | 'success' | 'failed';
  runId?: string;
  portfolio?: {
    currentPositions: number;
    availableCapital: number;
    riskCapacity: number;
  };
  signals?: {
    buy: number;
    hold: number;
    sell: number;
  };
  execution?: {
    ordersPlaced: number;
    ordersFailed: number;
    totalValue: number;
  };
}

interface CompletePipelineStatus {
  tradingUniverse: TradingUniversePipelineStatus;
  userScreening: UserScreeningPipelineStatus | null;
  timestamp: Date;
}

interface PipelineStatusMeta {
  telemetryAvailable?: boolean;
  telemetryMessage?: string;
}

/** Whether this stage is market-level (no stock list). From API stageMetadata.isMarketLevel only; no hardcoded list. */
const isRegimeIndicator = (
  stage: { id: string; inputCount?: number; outputCount?: number },
  stageMetadata?: Record<string, StageMetadata>
): boolean => {
  if ((stage.inputCount ?? 0) !== 0 || (stage.outputCount ?? 0) !== 0) return false;
  return stageMetadata?.[stage.id]?.isMarketLevel === true;
};

/** Sub-stage status for display (from API status). No hardcoded labels. */
const subStageStatusLabel = (status: string): string => {
  switch (status) {
    case 'success': return 'Pass';
    case 'failed': return 'Fail';
    case 'running': return 'Working';
    case 'pending': return 'Pending';
    case 'skipped': return 'Skipped';
    default: return status;
  }
};

const PipelineStatus: React.FC = () => {
  const [pipelineStatus, setPipelineStatus] = useState<CompletePipelineStatus | null>(null);
  const [meta, setMeta] = useState<PipelineStatusMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());

  const fetchPipelineStatus = async (isBackgroundRefresh = false) => {
    setError(null);
    // Only show full-page loading on initial load (no data yet). Background poll must not
    // set loading so we do not unmount content and scroll to top.
    if (!isBackgroundRefresh && !pipelineStatus) {
      setLoading(true);
    }
    try {
      const response = await api.get('/selectors/pipeline-status');

      if (response.data.success) {
        setPipelineStatus(response.data.data);
        setMeta(response.data.meta ?? { telemetryAvailable: true });
      } else {
        setError(response.data?.error || 'Failed to fetch pipeline status');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMessage = err?.response?.data?.error;
      const msg = typeof serverMessage === 'string'
        ? serverMessage
        : serverMessage?.message || null;

      if (status === 401) {
        setError(msg || 'Session expired or not logged in. Please log in again.');
      } else if (status === 403) {
        setError(msg || 'Access denied.');
      } else if (status && status >= 500) {
        setError(msg || 'Server error. Try again later.');
      } else if (!err?.response) {
        setError('Cannot reach server. Check that the backend is running and CORS allows this origin.');
      } else {
        setError(msg || err.message || 'Failed to fetch pipeline status');
      }
      console.error('Error fetching pipeline status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (pipelineType: 'tradingUniverse' | 'userScreening', stage: string) => {
    setRetrying(true);
    try {
      // Backend only supports full rebuild: always send stage 'all' for tradingUniverse so Retry Stage triggers a full rebuild
      const effectiveStage = pipelineType === 'tradingUniverse' ? 'all' : stage;
      const response = await api.post(
        '/selectors/pipeline/retry',
        { pipelineType, stage: effectiveStage }
      );
      
      if (response.data.success) {
        alert('Pipeline rebuild started. This may take up to 90 seconds.');
        // Refresh status after a delay (background so no loading/scroll reset)
        setTimeout(() => fetchPipelineStatus(true), 5000);
      }
    } catch (err: any) {
      console.error('Error retrying pipeline:', err);
      alert(err.response?.data?.error || 'Failed to retry pipeline');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    fetchPipelineStatus();
  }, []);

  // Default-expand all stages so every step is as detailed as Market Regime
  useEffect(() => {
    const stages = pipelineStatus?.tradingUniverse?.stages;
    if (stages?.length) {
      setExpandedStages(prev => {
        const next = new Set(prev);
        stages.forEach((s: PipelineStage) => next.add(s.id));
        return next;
      });
    }
  }, [pipelineStatus?.tradingUniverse?.stages]);

  // Poll every 5 seconds when pipeline is running (background refresh: no loading state, no scroll reset)
  useEffect(() => {
    const interval = setInterval(() => {
      if (pipelineStatus?.tradingUniverse?.status === 'running' ||
          pipelineStatus?.userScreening?.status === 'running') {
        fetchPipelineStatus(true);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [pipelineStatus?.tradingUniverse?.status, pipelineStatus?.userScreening?.status]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return '✓';
      case 'failed': return '✗';
      case 'running': return '⟳';
      case 'pending': return '○';
      case 'skipped': return '⊘';
      default: return '○';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'running': return 'text-blue-600 animate-spin';
      case 'pending': return 'text-gray-400';
      case 'skipped': return 'text-yellow-600';
      default: return 'text-gray-400';
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDateTime = (date: Date | null) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      dateStyle: 'short',
      timeStyle: 'short',
    }) + ' ET';
  };

  const toggleStageExpansion = (stageId: string) => {
    setExpandedStages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stageId)) {
        newSet.delete(stageId);
      } else {
        newSet.add(stageId);
      }
      return newSet;
    });
  };

  const getSubStageIcon = (type: string) => {
    switch (type) {
      case 'api_call': return '🔌';
      case 'filter': return '🔍';
      case 'calculation': return '📊';
      case 'enrichment': return '✨';
      default: return '⚙️';
    }
  };

  const getProviderBadge = (provider?: string) => {
    if (!provider) return null;
    const colors: Record<string, string> = {
      alpaca: 'bg-blue-900/40 text-blue-300 border border-blue-700',
      alpha_vantage: 'bg-purple-900/40 text-purple-300 border border-purple-700',
      polygon: 'bg-green-900/40 text-green-300 border border-green-700',
      internal: 'bg-neutral-700 text-neutral-300 border border-neutral-600',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[provider] || colors.internal}`}>
        {provider.toUpperCase().replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-neutral-400">Loading pipeline status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
        <p className="text-red-400 font-medium">Failed to fetch pipeline status</p>
        <p className="text-red-300 text-sm mt-1">{error}</p>
        <button
          type="button"
          onClick={() => fetchPipelineStatus()}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          aria-label="Retry fetching pipeline status"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!pipelineStatus) {
    return <div className="text-neutral-400">No pipeline data available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Trading Universe Pipeline */}
      <div className="bg-neutral-800 border border-neutral-700 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-neutral-700 bg-neutral-900">
          <h2 className="text-lg font-semibold text-white">Trading Universe Pipeline</h2>
          <div className="mt-2 text-sm text-neutral-400 space-y-1">
            <div>
              Last Build: {pipelineStatus.tradingUniverse.lastBuildTime
                ? formatDateTime(pipelineStatus.tradingUniverse.lastBuildTime)
                : pipelineStatus.tradingUniverse.lastStoredUniverse
                  ? `Never (stored universe from ${formatDateTime(new Date(pipelineStatus.tradingUniverse.lastStoredUniverse.createdAt))}, ${pipelineStatus.tradingUniverse.lastStoredUniverse.totalStocks} stocks)`
                  : 'Never'}
            </div>
            <div>Next Build: {formatDateTime(pipelineStatus.tradingUniverse.nextBuildTime)}</div>
            <div>Scheduled: {pipelineStatus.tradingUniverse.scheduledTimes.length > 0 ? pipelineStatus.tradingUniverse.scheduledTimes.join(', ') + ' ET' : '—'}</div>
            <div className="flex items-center gap-3 flex-wrap">
              <span>Status: <span className={getStatusColor(pipelineStatus.tradingUniverse.status)}>{pipelineStatus.tradingUniverse.status.toUpperCase()}</span></span>
              {pipelineStatus.tradingUniverse.regimeLabel && (
                <span className="px-2 py-0.5 rounded bg-neutral-700 text-neutral-300 text-xs font-medium">
                  Regime: {pipelineStatus.tradingUniverse.regimeLabel}
                </span>
              )}
              {pipelineStatus.tradingUniverse.buildId && (
                <span className="text-neutral-500 text-xs">Build: {pipelineStatus.tradingUniverse.buildId}</span>
              )}
            </div>
            {pipelineStatus.tradingUniverse.status === 'running' && !pipelineStatus.tradingUniverse.lastBuildTime && (
              <div className="text-amber-400 text-xs mt-1">First run in progress.</div>
            )}
          </div>
        </div>

        {/* Pipeline steps: all values from API (pipeline-status). Stage order from backend (TRADING_UNIVERSE_STAGES).
            Step 0 = Regime detection (stage0). Steps 1-13 = stock stages (Universe fetch, Polygon type set, Eligibility, Liquidity, Price, Market data, Fundamentals, Earnings zone, Sentiment, Scoring, Diversification, Stability, Store and QA).
            Sub-step labels (Pass/Working/Fail) from subStage.status; counts from stage.inputCount/outputCount. */}
        <div className="p-6 space-y-4">
          {pipelineStatus.tradingUniverse.stages.length === 0 ? (
            <div className="text-neutral-400 text-center py-8">
              No pipeline data available. Waiting for next scheduled build.
              {pipelineStatus.tradingUniverse.lastStoredUniverse && (
                <p className="mt-2 text-neutral-500 text-sm">
                  Current stored universe: {pipelineStatus.tradingUniverse.lastStoredUniverse.totalStocks} stocks (stored {formatDateTime(new Date(pipelineStatus.tradingUniverse.lastStoredUniverse.createdAt))}).
                </p>
              )}
            </div>
          ) : (
            pipelineStatus.tradingUniverse.stages.map((stage, stepIndex) => {
              const isRegime = stage.id === 'stage0' || stage.id === 'regime_detection';
              const input = stage.inputCount ?? 0;
              const output = stage.outputCount ?? 0;
              const totalIndicators = isRegime ? input : 0;
              const passingIndicators = isRegime ? output : 0;
              const failingIndicators = isRegime ? totalIndicators - passingIndicators : 0;
              const pct = !isRegime && input > 0 ? (output / input) * 100 : (stage.conversionRate ?? 0);
              const pctLabel = !isRegime && (input > 0 || output > 0) ? `${pct.toFixed(1)}%` : null;
              const hasApiSubSteps = Boolean(stage.subStages && stage.subStages.length > 0);
              const stageMeta = pipelineStatus.tradingUniverse.stageMetadata?.[stage.id];
              const detailSubSteps = stageMeta?.processSteps;
              const hasStaticDetail = Boolean(detailSubSteps?.length);
              const subStepCount = hasApiSubSteps ? stage.subStages!.length : (detailSubSteps?.length ?? 0);
              const hasAnyDetail = hasApiSubSteps || hasStaticDetail;
              const isExpanded = expandedStages.has(stage.id);
              return (
                <article
                  key={stage.id}
                  className="border border-neutral-700 rounded-lg overflow-hidden bg-neutral-800"
                  aria-label={`Step ${stepIndex}: ${stage.name}`}
                >
                  <div
                    className={`p-4 ${hasAnyDetail ? 'cursor-pointer hover:bg-neutral-700/50' : ''} transition-colors`}
                    onClick={() => hasAnyDetail && toggleStageExpansion(stage.id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex items-center justify-center w-10 h-10 rounded bg-neutral-700 text-neutral-200 font-semibold shrink-0" aria-hidden="true">
                        {stepIndex}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-2xl ${getStatusColor(stage.status)}`} aria-hidden="true">
                            {getStatusIcon(stage.status)}
                          </span>
                          <h3 className="font-medium text-white">{stage.name}</h3>
                          <span className="px-1.5 py-0.5 rounded bg-neutral-700/80 text-neutral-400 text-xs font-mono" title="Backend stage id">
                            {stage.id}
                          </span>
                          {hasAnyDetail && subStepCount > 0 && (
                            <span className="text-neutral-400 text-sm">
                              {isExpanded ? '▼' : '▶'} {subStepCount} sub-steps
                            </span>
                          )}
                        </div>
                        {stageMeta?.purpose != null && stageMeta?.apiSummary != null && (
                          <div className="mt-1.5 text-xs text-neutral-500 space-y-0.5">
                            <p>{stageMeta.purpose}</p>
                            <p className="font-mono text-neutral-600">API: {stageMeta.apiSummary}</p>
                          </div>
                        )}
                        <div className="mt-2 text-sm text-neutral-400 space-y-1">
                          {isRegime ? (
                            <div className="flex flex-col gap-0.5">
                              <p>
                                <span className="text-white font-medium">{passingIndicators}</span> of <span className="text-white font-medium">{totalIndicators}</span> indicators passing
                                {failingIndicators > 0 && (
                                  <span className="text-amber-500 ml-2">({failingIndicators} of {totalIndicators} failing)</span>
                                )}
                                <span className={(stage.conversionRate ?? 0) < 80 ? 'text-orange-600' : 'text-green-600'}>({(stage.conversionRate ?? 0).toFixed(1)}%)</span>
                              </p>
                              <p className="text-xs text-neutral-500">Market-level indicators (no stock list).</p>
                            </div>
                          ) : isRegimeIndicator(stage, pipelineStatus.tradingUniverse.stageMetadata) ? (
                            <div className="flex flex-col gap-0.5">
                              <span className={stage.status === 'success' ? 'text-green-600' : 'text-amber-500'}>
                                Market-level indicator. {stage.status === 'success' ? 'Pass.' : stage.status === 'failed' ? 'Fail.' : stage.status}
                              </span>
                              <p className="text-xs text-neutral-500">Does not process the stock universe.</p>
                            </div>
                          ) : (
                            <>
                              <p>
                                <span className="text-white font-medium">{input.toLocaleString()}</span> stocks → <span className="text-white font-medium">{output.toLocaleString()}</span> stocks
                                {pctLabel != null && (
                                  <span className={pct < 50 ? 'text-orange-400' : 'text-neutral-400'}> ({pctLabel})</span>
                                )}
                              </p>
                              {stage.filteredReasons && stage.filteredReasons.length > 0 && (
                                <p className="text-xs text-neutral-500">Filters applied: {stage.filteredReasons.join(', ')}</p>
                              )}
                            </>
                          )}
                          {stage.duration > 0 && (
                            <p className="text-xs text-neutral-500">Duration: {formatDuration(stage.duration)}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    {stage.error && (
                      <div className="mt-3 p-3 bg-red-900/20 border border-red-700 rounded">
                        <div className="font-medium text-red-400">Error: {stage.error.message}</div>
                        <div className="text-sm text-red-300 mt-1">Code: {stage.error.code}</div>
                        {stage.error.retryable && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRetry('tradingUniverse', stage.id); }}
                            disabled={retrying}
                            className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {retrying ? 'Retrying...' : 'Retry Stage'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {isExpanded && hasApiSubSteps && (
                    <div className="bg-neutral-900 border-t border-neutral-700 px-4 py-3">
                      <p className="text-xs text-neutral-500 mb-2 font-medium">Sub-steps (pipeline telemetry)</p>
                      <ul className="space-y-2" role="list">
                        {stage.subStages!.map((subStage) => (
                          <li key={subStage.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm py-1 border-b border-neutral-800 last:border-0">
                            <span className={getStatusColor(subStage.status)} aria-hidden="true">{getStatusIcon(subStage.status)}</span>
                            <span className="text-white font-medium min-w-[140px]">{subStage.name}</span>
                            <span className="text-neutral-500 text-xs capitalize">{subStage.type.replace('_', ' ')}</span>
                            {subStage.provider && getProviderBadge(subStage.provider)}
                            <span className={`text-xs font-medium ${subStage.status === 'success' ? 'text-green-500' : subStage.status === 'failed' ? 'text-red-400' : 'text-neutral-400'}`}>
                              {subStageStatusLabel(subStage.status)}
                            </span>
                            {subStage.type === 'api_call' && (subStage.inputCount == null || subStage.inputCount === 0) && subStage.outputCount != null && (
                              <span className="text-neutral-500 text-xs">({subStage.outputCount.toLocaleString()} items)</span>
                            )}
                            {subStage.inputCount != null && subStage.outputCount != null && subStage.inputCount > 0 && (
                              <span className="text-neutral-500 text-xs">
                                {subStage.inputCount.toLocaleString()} → {subStage.outputCount.toLocaleString()} ({((subStage.outputCount / subStage.inputCount) * 100).toFixed(1)}%)
                              </span>
                            )}
                            {subStage.duration != null && subStage.duration > 0 && (
                              <span className="text-neutral-500 text-xs">{formatDuration(subStage.duration)}</span>
                            )}
                            {subStage.error && <span className="text-red-400 text-xs w-full mt-0.5">Error: {subStage.error}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isExpanded && hasStaticDetail && !hasApiSubSteps && (
                    <div className="bg-neutral-900 border-t border-neutral-700 px-4 py-3">
                      <p className="text-xs text-neutral-500 mb-2 font-medium">Process detail (from pipeline design)</p>
                      <ul className="space-y-2" role="list">
                        {detailSubSteps!.map((step, idx) => (
                          <li key={`${stage.id}-${idx}-${step.name}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm py-1 border-b border-neutral-800 last:border-0">
                            <span className="text-green-500" aria-hidden="true">&#10003;</span>
                            <span className="text-white font-medium min-w-[140px]">{step.name}</span>
                            <span className="text-neutral-500 text-xs capitalize">{step.type.replace('_', ' ')}</span>
                            {step.provider && getProviderBadge(step.provider)}
                            <span className="text-neutral-500 text-xs flex-1">{step.description}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </article>
              );
            })
          )}

          {/* How the final universe is selected (supports stage0-13 and legacy stage2-5) */}
          {pipelineStatus.tradingUniverse.finalCount > 0 && pipelineStatus.tradingUniverse.stages.length > 0 && (() => {
            const stages = pipelineStatus.tradingUniverse.stages;
            const marketDataStage = stages.find(s => s.id === 'stage6') ?? stages.find(s => s.id === 'stage2');
            const scoringStage = stages.find(s => s.id === 'stage10') ?? stages.find(s => s.id === 'stage3');
            const divStage = stages.find(s => s.id === 'stage11') ?? stages.find(s => s.id === 'stage4');
            const stabilityStage = stages.find(s => s.id === 'stage12') ?? stages.find(s => s.id === 'stage5');
            const nameMarket = marketDataStage?.name;
            const nameScoring = scoringStage?.name;
            const nameDiv = divStage?.name;
            const nameStability = stabilityStage?.name;
            return (
            <div className="mt-4 p-4 rounded-lg bg-neutral-900 border border-neutral-700">
              <h4 className="text-sm font-semibold text-white mb-2">How the final {pipelineStatus.tradingUniverse.finalCount} stocks are selected</h4>
              <p className="text-sm text-neutral-400">
                {nameMarket ? `The eligible pool from ${nameMarket} is` : 'The eligible pool is'} scored and ranked{nameScoring ? ` (${nameScoring}: factor scores, relative strength vs SPY, sector rotation).` : ' (factor scores, relative strength vs SPY, sector rotation).'}
                {nameDiv && <strong className="text-neutral-300"> {nameDiv} </strong>}
                {nameDiv ? ' selects' : ' Sector diversification selects'} the final {pipelineStatus.tradingUniverse.finalCount} by filling sector quotas up to the regime target size.
                {nameStability && <strong className="text-neutral-300"> {nameStability} </strong>}
                {nameStability ? ' assigns' : ' Stability assigns'} sticky seats (carried over) and fresh seats (new), then the universe is stored.
              </p>
              {(() => {
                const fromMarket = marketDataStage?.outputCount ?? 0;
                const fromScoring = scoringStage?.outputCount ?? 0;
                const fromDiv = divStage?.outputCount ?? 0;
                const fromStability = stabilityStage?.outputCount ?? 0;
                if (fromMarket > 0 || fromScoring > 0 || fromDiv > 0) {
                  return (
                    <p className="text-xs text-neutral-500 mt-2 font-mono">
                      This run: {fromMarket > 0 && `${fromMarket.toLocaleString()} after market data`}
                      {fromScoring > 0 && ` → ${fromScoring.toLocaleString()} scored`}
                      {fromDiv > 0 && ` → ${fromDiv.toLocaleString()} diversified`}
                      {fromStability > 0 && ` → ${fromStability.toLocaleString()} final`}
                    </p>
                  );
                }
                return null;
              })()}
            </div>
            );
          })()}

          {pipelineStatus.tradingUniverse.totalDuration > 0 && (
            <div className="mt-4 pt-4 border-t border-neutral-700">
              <div className="flex justify-between items-center">
                <span className="font-medium text-white">Total Duration:</span>
                <span className="text-neutral-300">{formatDuration(pipelineStatus.tradingUniverse.totalDuration)}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="font-medium text-white">Final Universe:</span>
                <span className="text-neutral-300">{pipelineStatus.tradingUniverse.finalCount} stocks</span>
              </div>
              {pipelineStatus.tradingUniverse.seatAllocation && (
                <div className="flex justify-between items-center mt-2">
                  <span className="font-medium text-white">Seat Allocation:</span>
                  <span className="text-neutral-300">
                    Sticky: {pipelineStatus.tradingUniverse.seatAllocation.sticky}, 
                    Fresh: {pipelineStatus.tradingUniverse.seatAllocation.fresh}
                  </span>
                </div>
              )}
            </div>
          )}

          {pipelineStatus.tradingUniverse.status === 'failed' && (
            <div className="mt-4">
              <button
                onClick={() => handleRetry('tradingUniverse', 'all')}
                disabled={retrying}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {retrying ? 'Rebuilding...' : 'Rebuild Trading Universe'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* User Screening Pipeline */}
      {pipelineStatus.userScreening && (
        <div className="bg-neutral-800 border border-neutral-700 rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-neutral-700 bg-neutral-900">
            <h2 className="text-lg font-semibold text-white">User Screening Pipeline (Last Run)</h2>
            <div className="mt-2 text-sm text-neutral-400 space-y-1">
              <div>Last Run: {formatDateTime(pipelineStatus.userScreening.lastRunTime)}</div>
              <div>Status: <span className={getStatusColor(pipelineStatus.userScreening.status)}>{pipelineStatus.userScreening.status.toUpperCase()}</span></div>
              {pipelineStatus.userScreening.portfolio && (
                <div>
                  Positions: {pipelineStatus.userScreening.portfolio.currentPositions}, 
                  Available: ${pipelineStatus.userScreening.portfolio.availableCapital.toFixed(2)}, 
                  Capacity: {pipelineStatus.userScreening.portfolio.riskCapacity}
                </div>
              )}
            </div>
          </div>

          <div className="p-6 space-y-4">
            {pipelineStatus.userScreening.stages.length === 0 ? (
              <div className="text-neutral-400 text-center py-8">
                No screening data available yet.
              </div>
            ) : (
              pipelineStatus.userScreening.stages.map((stage) => (
                <div key={stage.id} className="border border-neutral-700 rounded-lg p-4 bg-neutral-800 hover:bg-neutral-700 transition-colors">
                  <div className="flex items-center space-x-3">
                    <span className={`text-2xl ${getStatusColor(stage.status)}`}>
                      {getStatusIcon(stage.status)}
                    </span>
                    <div className="flex-1">
                      <h3 className="font-medium text-white">{stage.name}</h3>
                      {stage.duration > 0 && (
                        <div className="text-sm text-neutral-400">
                          Duration: {formatDuration(stage.duration)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {pipelineStatus.userScreening.signals && (
              <div className="mt-4 pt-4 border-t border-neutral-700">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-400">{pipelineStatus.userScreening.signals.buy}</div>
                    <div className="text-sm text-neutral-400">BUY Signals</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-400">{pipelineStatus.userScreening.signals.hold}</div>
                    <div className="text-sm text-neutral-400">HOLD Signals</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-400">{pipelineStatus.userScreening.signals.sell}</div>
                    <div className="text-sm text-neutral-400">SELL Signals</div>
                  </div>
                </div>
              </div>
            )}

            {pipelineStatus.userScreening.execution && (
              <div className="mt-4 pt-4 border-t border-neutral-700">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-white">Orders Placed:</span>
                  <span className="text-neutral-300">{pipelineStatus.userScreening.execution.ordersPlaced}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="font-medium text-white">Orders Failed:</span>
                  <span className="text-red-400">{pipelineStatus.userScreening.execution.ordersFailed}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => fetchPipelineStatus()}
          className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-neutral-900 transition-colors"
          aria-label="Refresh pipeline status"
        >
          Refresh Status
        </button>
      </div>
    </div>
  );
};

export default PipelineStatus;
